/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { inject, Injectable } from '@angular/core';
import { type ActivatedRouteSnapshot, type CanActivateChildFn, type CanActivateFn, type CanMatchFn, type Route, Router } from '@angular/router';
import { CORE_CONFIG, EventService, HttpService, LocalStorageService, PermissionService, SessionStorageService } from '@dm/core';
import { LicenseKey } from 'app/common/constants/license-key';
import { FeatureConfig } from 'app/config/feature';
import { ProductConfig, ShardSwitch, SWITCH } from 'app/config/switch';
import { PermitService } from 'app/feature/settings/users-security/user-role/role/permit.service';
import { help as highHelp } from 'assets/help/high/help_resource.js';
import { help as vstoreHighHelp } from 'assets/help/high/help_resource_diff.js';
import { help as middleHelp } from 'assets/help/middle/help_resource.js';
// 租户视图高中端
import { help as vstoreMiddleHelp } from 'assets/help/middle/help_resource_diff.js';
import { type Observable, forkJoin, of } from 'rxjs';
import { catchError, flatMap, map, tap } from 'rxjs/operators';

import { SESSION_KEY } from '../constants/sso.const';
import { SmartGuiService, ToolService } from '../services';
import { AuthService } from '../services/auth.service';
import { GlobalDataService } from '../services/global-data.service';
import { TaskService } from '../services/task.service';

@Injectable({ providedIn: 'root' })
class AuthGuard {
    constructor(
        private authService: AuthService,
        private permissionService: PermissionService,
        private router: Router,
        private eventService: EventService,
        private httpService: HttpService,
        private globalDataService: GlobalDataService,
        private taskService: TaskService,
        private smartGuiService: SmartGuiService,
        private sessionStorageService: SessionStorageService,
        private localStorageService: LocalStorageService,
        private permitService: PermitService,
        private toolService: ToolService,
    ) { }

    /**
     * 检查是否隐藏GUI
     */
    hideGuiCheck(): void {
        if (FeatureConfig.IsHideSmartGui) {
            SWITCH.SUPPORT_SMART_GUI = false;
        }
    }

    // 检查当前登录情况
    checkLogin(path: string): Observable<boolean> {
        const isAuthPass = this.authService.isAuth();
        let vstoreId = this.authService.getVstoreId();
        const sessionVstoreId = this.localStorageService.get('vstoreSystemId');
        SWITCH.IS_VIRTUAL_MACHINE_CONSOLE = path === 'virtualmachine';
        // 微存储、2910不支持GUI
        this.hideGuiCheck();

        // 租户系统直接设置session
        if (vstoreId) {
            SWITCH.IS_VSTORE_SYSTEM = true;
            SWITCH.IS_SMARTTHIN_IMPORT = this.permissionService.isLicenseExist([LicenseKey.SmartThin]);
            // layout框架需要
            this.sessionStorageService.set('vstoreId', vstoreId);
            // 租户系统刷新场景需要
            this.localStorageService.set('vstoreSystemId', vstoreId);
        }
        // 刷新场景读取session判断是否租户系统
        if (sessionVstoreId) {
            // layout框架需要
            this.sessionStorageService.set('vstoreId', sessionVstoreId);
            SWITCH.IS_VSTORE_SYSTEM = true;
            vstoreId = sessionVstoreId;
        }

        // 如果是FCV精准跳转，则跳转到目标路径
        if (FeatureConfig.IsSupportFCVLocation && window.location.href.includes(SESSION_KEY) && isAuthPass &&
            ShardSwitch.SUPPORT_FCV_SSO_JUMP) {
            this.authService.convertParams();
            const targetPath = this.authService.getTargetPath();
            if (targetPath) {
                window.location.href = targetPath;
            }
        }

        // 登录页
        if (path === 'login') {
            const params = this.getUrlParams();
            const isSAMLLogin = params.ssoType === 'SAML';
            // 已登录过且不为SAML协议跳转，则跳转到首页
            if (isAuthPass && !isSAMLLogin) {
                if (!vstoreId) {
                    this.router.navigate(['/home']);
                } else if (vstoreId) {
                    this.router.navigate(['/vstore/home']);
                } else { }
                return of(false);
            }
            return of(true);
        }

        // 开工失败
        if (path === 'poweronfail') {
            return of(true);
        }

        // sso单点登录
        if (path === 'login/sso') {
            return of(true);
        }

        // 登录不通过跳转到登录页
        if (!isAuthPass) {
            this.authService.clearLoginInfo();
            this.eventService.emit('logout');
            return of(false);
        }

        // 不支持极简模式场景，simple路由跳转到标准模式首页。处理手动修改URL的情况
        if (path === 'simple' && !ProductConfig.IsSupportSimpleMode) {
            this.router.navigate(['/home']);
            return of(false);
        }

        // 登录通过先查是否在升级，以决定get请求的报错是否展示
        return this.globalDataService.getSystemInfoSync()
            .pipe(
                catchError(() => this.loadInitInfo()),
                flatMap(() => this.loadInitInfo()),
            );
    }

    // 加载初始化信息
    loadInitInfo(): Observable<boolean> {
        if (!this.authService.initInfoLoaded) {
            return forkJoin([
                this.authService.initLoginInfo(),
                this.globalDataService.initLicenseFeature(),
                this.globalDataService.initTime(),
                this.smartGuiService.init(),
            ])
                .pipe(
                    flatMap(() => this.initPermissionInfo()),
                    map(() => true),
                    tap(() => {
                        CORE_CONFIG.userId = this.authService.getHashUserId(this.authService.getUserInfo().userid);
                        this.setHelp();
                        this.globalDataService.init();
                        this.taskService.init();
                        this.authService.initInfoLoaded = true;

                        this.toolService.updateAntiransomwareFlag().subscribe();
                    }, () => {
                        // 登录过之后退出需要发送delete session退出会话
                        this.eventService.emit('logout', { sendDeleteSession: true });
                    }),
                );
        }
        return of(true);
    }

    /**
     * 获取权限列表
     */
    initPermissionInfo(): Observable<any> {
        const roleId = this.authService.getRoleId();
        const authMode = this.authService.getAuthMode();
        const rest = this.httpService.makeUrl('role.get').format(roleId);
        this.permissionService.setRoleId(roleId);
        this.permissionService.setAuthMode(authMode);
        return this.httpService.get(rest)
            .pipe(
                tap(({ data }) => {
                    const permitList = this.permitService.stringToSimpleMap(data.rolePermitList, true);
                    const roleKeys = Object.keys(permitList);
                    const permitListStr = [];
                    for (let i = 0, len = roleKeys.length; i < len; i++) {
                        permitList[roleKeys[i]].forEach((item) => permitListStr.push(item));
                    }

                    // 微存储【namespace ---> lun】
                    let permitListResult = data.permitList;
                    if (FeatureConfig.IsReplaceStrLunWithNamespace) {
                        permitListResult = permitListResult.replace(/namespace/ig, 'lun');
                    }
                    this.permissionService.setRolePermission(permitListStr, permitListResult);
                }),
            );
    }

    /**
     * 加载新手指南
     */
    loadWalkfast(): void {
        const walkfastNode = document.createElement('script');
        walkfastNode.setAttribute('src', 'assets/help/walkfast_user_guide/walkfast_index_https.js');
        document.body.append(walkfastNode);
    }

    /**
     * 设置帮助链接。需要区分中高端
     */
    setHelp(): void {
        let sub$ = this.eventService.subscribe('systemInfoUpdate', () => {
            const isHigh = FeatureConfig.IsHighProdHelp;

            if (isHigh === undefined) {
                return;
            }
            const prodType = isHigh ? 'high' : 'middle';
            // 租户视图和系统视图各一套联机帮助
            CORE_CONFIG.helpMap = SWITCH.IS_VSTORE_SYSTEM ?
                {
                    rootPath: `/assets/help/${prodType}/vstore`,
                    rootPathEvent: `/assets/help/event`,
                    help: isHigh ? vstoreHighHelp : vstoreMiddleHelp,
                    event: {
                        zh: 'Event_help_bookmap_zh.html',
                        en: 'Event_help_bookmap_en.html',
                    },
                } :
                {
                    rootPath: `/assets/help/${prodType}`,
                    rootPathEvent: `/assets/help/event`,
                    help: isHigh ? highHelp : middleHelp,
                    event: {
                        zh: 'Event_help_bookmap_zh.html',
                        en: 'Event_help_bookmap_en.html',
                    },
                };

            sub$.unsubscribe();
            sub$ = null;
        });
    }

    getUrlParams(): { ssoType?: string } {
        const paramsStr = location.search.substr(1).split('&');
        const params = {};
        paramsStr.forEach((str) => {
            const kv = str.split('=');
            params[kv[0]] = kv[1];
        });

        return params;
    }
}

export const AuthGuardCanLoad: CanMatchFn = (route: Route): Observable<boolean> => inject(AuthGuard).checkLogin(route.path);

export const AuthGuardCanActivate: CanActivateFn = (route: ActivatedRouteSnapshot): Observable<boolean> =>
    inject(AuthGuard).checkLogin(route.routeConfig.path);

export const AuthGuardCanActivateChild: CanActivateChildFn = (route: ActivatedRouteSnapshot): Observable<boolean> =>
    inject(AuthGuard).checkLogin(route.routeConfig.path);

